import { Prompt, SnackbarStatus, Typography, useSnackbar } from '@weareopenr/component-library';
import { EVENT_TYPES, IEvent } from '../../api/getEvents';
import { useState } from 'react';
import styles from './CancelScheduledProductPublicationPrompt.module.scss';
import { API_URL } from '../../api';
import { fetch } from '../../api/fetchWrapper/fetch';
import { Status } from '../../api/constants';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../hooks/QueryKeys';
import { formatDateTimeDayJs } from '../../helpers/formatDateTime/formatDateTimeDayJs';

interface ICancelScheduledProductPublicationPromptProps {
  event: IEvent | null;
  isOpen: boolean;
  closePrompt: () => void;
}

const getPublicationTypeCopy = (event: IEvent) => {
  if (event.child_events === null) {
    return 'Unknown publication type';
  }
  const hasProductPublish = event.child_events.some(
    (childEvent) => childEvent.event_type === EVENT_TYPES.PUBLISH_PRODUCT,
  );
  const hasPricingPublish = event.child_events.some(
    (childEvent) => childEvent.event_type === EVENT_TYPES.PUBLISH_PRICING,
  );
  if (hasProductPublish && hasPricingPublish) return 'Product & Pricing Data';
  if (hasProductPublish) return 'Product Data';
  return 'Pricing Data';
};

const getEventDate = (event: IEvent): string => {
  return event.message.split('Publish scheduled for ')[1];
};

export const Content = ({ event }: Omit<ICancelScheduledProductPublicationPromptProps, 'isOpen' | 'closePrompt'>) => {
  return (
    <div className={styles['center-container']}>
      <Typography className={styles['padding-bottom']}>You will cancel the publication of the following:</Typography>
      <Typography weight="bold">{getPublicationTypeCopy(event)}</Typography>
      <Typography>Scheduled to be published at</Typography>
      <Typography>{formatDateTimeDayJs(new Date(getEventDate(event)))}</Typography>
    </div>
  );
};

export const CancelScheduledProductPublicationPrompt = ({
  event,
  closePrompt,
  isOpen,
}: ICancelScheduledProductPublicationPromptProps) => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleCancel = async () => {
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/publish/products/scheduled`, { method: 'DELETE' });
      if (res.status !== Status.SUCCESS) {
        throw new Error('Unable to cancel publication');
      }
      queryClient.invalidateQueries({ queryKey: [QueryKeys.events] });
      showSnackbar({ status: SnackbarStatus.SUCCESS, message: 'Successfully cancelled publication' });
      closePrompt();
    } catch (e) {
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'An error occurred while cancelling the publication' });
      setIsSubmitting(false);
    }
  };

  return (
    <Prompt
      closePrompt={closePrompt}
      open={isOpen}
      promptHeading="Are you sure you want to cancel the publication?"
      backButton
      backButtonText="Back"
      buttonHandler={handleCancel}
      isSubmitting={isSubmitting}
      buttonText="Cancel schedule"
      size="small"
    >
      <Content event={event} />
    </Prompt>
  );
};
